import React, { useState, useCallback, useEffect } from "react";
import { getAssetUrl } from "../utils/asset";
import { getTenantId } from "../utils/tenant";

/**
 * S3Image Component
 *
 * A React component for displaying images from S3 with proper authentication headers.
 * This component fetches images with the required headers (X-Manager-ID, X-Config-ID, X-Tenant)
 * and displays them using blob URLs for proper authentication.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <S3Image keyPath="team-logos/team-123.png" alt="Team Logo" />
 *
 * // With fallback
 * <S3Image
 *   keyPath="team-logos/team-123.png"
 *   alt="Team Logo"
 *   fallbackSrc="/assets/default-team.png"
 * />
 *
 * // Using specialized components
 * <TeamLogo keyPath="team-logos/team-123.png" alt="Team Logo" />
 * <PersonAvatar keyPath="players/player-456.jpg" alt="Player Photo" />
 * ```
 */

interface S3ImageProps {
  keyPath?: string | null;
  alt?: string;
  className?: string;
  fallbackSrc?: string;
  loading?: "lazy" | "eager";
  onError?: () => void;
  onLoad?: () => void;
  placeholder?: React.ReactNode;
  sizes?: string;
  quality?: number;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
}

interface ImageError {
  hasError: boolean;
  retryCount: number;
}

export const S3Image: React.FC<S3ImageProps> = ({
  keyPath,
  alt = "",
  className = "",
  fallbackSrc = "/assets/placeholder.png",
  loading = "lazy",
  onError,
  onLoad,
  placeholder,
  sizes,
  width,
  height,
  style,
}) => {
  const [imageState, setImageState] = useState<ImageError>({
    hasError: false,
    retryCount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const assetUrl = useLoadImage(keyPath);

  // Handle image load success
  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setImageState((prev) => ({ ...prev, hasError: false }));
    onLoad?.();
  }, [onLoad]);

  // Handle image load error
  const handleError = useCallback(() => {
    if (imageState.retryCount < 2) {
      // Retry loading the image
      setImageState((prev) => ({
        hasError: false,
        retryCount: prev.retryCount + 1,
      }));
    } else {
      // Give up and show fallback
      setImageState((prev) => ({ ...prev, hasError: true }));
      setIsLoading(false);
      onError?.();
    }
  }, [imageState.retryCount, onError]);

  // If no keyPath and no fallback, don't render
  if ((!keyPath && !fallbackSrc) || !assetUrl) {
    return null;
  }

  return (
    <div className={`relative ${className}`} style={style}>
      <img
        src={assetUrl}
        alt={alt}
        className={`transition-opacity duration-300 ${
          isLoading ? "opacity-0" : "opacity-100"
        } ${className}`}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError}
        sizes={sizes}
        width={width}
        height={height}
      />

      {isLoading && !placeholder && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center rounded-sm" />
      )}
    </div>
  );
};

// Specialized components for common use cases
export const TeamLogo: React.FC<Omit<S3ImageProps, "fallbackSrc">> = (
  props
) => (
  <S3Image
    {...props}
    fallbackSrc="/assets/team-placeholder.png"
    className={`w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 rounded object-contain ${
      props.className || ""
    }`}
    placeholder={
      <div className="w-8 h-8 md:w-10 md:h-10 lg:w-15 lg:h-15 bg-gray-200 rounded flex items-center justify-center">
        <span className="text-gray-400 text-xs">LOGO</span>
      </div>
    }
  />
);

export const TeamCover: React.FC<Omit<S3ImageProps, "fallbackSrc">> = (
  props
) => (
  <S3Image
    {...props}
    fallbackSrc="/assets/team-cover-placeholder.png"
    className={`w-full h-32 object-cover ${props.className || ""}`}
    placeholder={
      <div className="w-full h-32 bg-gray-200 flex items-center justify-center">
        <span className="text-gray-400">Team Cover</span>
      </div>
    }
  />
);

export const useLoadImage = (keyPath: string | undefined | null) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    const assetUrl = getAssetUrl(keyPath);
    if (!assetUrl) {
      return;
    }
    // Fetch the image
    fetch(assetUrl, {
      headers: {
        "X-Manager-ID": "tenant-web",
        "X-Tenant": getTenantId(),
      },
    })
      .then((res) => res.json())
      .then((data) => setImageUrl(data.url));
  }, [keyPath]);

  return imageUrl;
};
